"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.JsonTemplateCompiler = void 0;
/** Compiles a JSON template into js code. */
function sanitizeLocalTemplateName(templateId) {
    return templateId.replace(/[.?/]/g, '_');
}
/* eslint @typescript-eslint/no-explicit-any:0 */
class JsonTemplateCompiler {
    compile(templateJson, manifestDependencies = {}) {
        const { templates = [], sub_templates = {}, bundles = [], sub_bundles = {}, libraries = [], strings = {}, assets: { text = [], json = [], binary = [] } = {}, } = manifestDependencies;
        // Libraries dependencies
        if (libraries.length > 0)
            throw new Error('Libraries are not supported yet');
        const dependencies = [
            ...templates,
            ...Object.keys(sub_templates),
            ...bundles,
            ...Object.keys(sub_bundles),
            ...Object.keys(strings),
            ...[...text, ...json, ...binary].map((asset) => `./${asset}`),
            ...[...text, ...json, ...binary].map((asset) => `./${asset}?url`),
        ];
        const dependenciesMap = {};
        dependencies.forEach((dependency) => {
            const localName = sanitizeLocalTemplateName(dependency.split('~')[0]);
            dependenciesMap[localName] = dependency;
        });
        return this.compileTemplate(templateJson, dependenciesMap);
    }
    compileTemplate(json, dependenciesMap) {
        const code = [];
        // Generate the renderer
        // Generated code example: exports.default = ({a}) => ({1,a});
        const dependencies = new Set();
        const parameters = new Set();
        const nodeCode = this.generateNodeCode(json, parameters, dependencies);
        const parametersDefinition = parameters.size === 0 ? '' : `{${Array.from(parameters).join(',')}}`;
        // Generate imports. Ex: const a = require("templates.a.1_0").default;
        dependencies.forEach((localName) => {
            const moduleName = dependenciesMap[localName];
            if (moduleName === undefined)
                throw new Error(`Missing dependency ${localName}`);
            code.push(`const ${localName} = require("${moduleName}");`);
        });
        code.push(`exports.default = (${parametersDefinition}) => (${nodeCode});`);
        return code.join('\n');
    }
    generateNodeCode(node, parameters, dependencies) {
        if (Array.isArray(node)) {
            return this.generateArrayCode(node, parameters, dependencies);
        }
        else if (typeof node === 'object') {
            return this.generateObjectCode(node, parameters, dependencies);
        }
        return JSON.stringify(node);
    }
    /** Generated code example: {1,a.default("r")} */
    generateObjectCode(object, parameters, dependencies) {
        const keys = Object.keys(object);
        const memberCode = {};
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (key === '_parameter') {
                if (keys.length !== 1)
                    throw new Error('_parameter should be the only key');
                return this.generateParameterCode(object[key], parameters);
            }
            else if (key === '_value') {
                if (keys.length !== 1)
                    throw new Error('_value should be the only key');
                return this.generateValueCode(sanitizeLocalTemplateName(object[key]), dependencies);
            }
            else if (key.startsWith('_render:')) {
                if (keys.length !== 1)
                    throw new Error('_render should be the only key');
                const templateName = key.slice('_render:'.length);
                return this.generateTemplateCode(sanitizeLocalTemplateName(templateName), this.generateNodeCode(object[key], parameters, dependencies), dependencies);
            }
            memberCode[key] = this.generateNodeCode(object[key], parameters, dependencies);
        }
        let firstElement = true;
        const result = ['{'];
        for (let i = 0; i < keys.length; i++) {
            if (firstElement) {
                firstElement = false;
            }
            else {
                result.push(',');
            }
            const key = keys[i];
            result.push(`"${key}":${memberCode[key]}`);
        }
        result.push('}');
        return result.join('');
    }
    /** Generated code example: [1,a.default("r")] */
    generateArrayCode(array, parameters, dependencies) {
        let firstElement = true;
        const result = ['['];
        for (let i = 0; i < array.length; i++) {
            if (firstElement) {
                firstElement = false;
            }
            else {
                result.push(',');
            }
            result.push(this.generateNodeCode(array[i], parameters, dependencies));
        }
        result.push(']');
        return result.join('');
    }
    /** Generated code example: a */
    generateParameterCode(parameterName, parameters) {
        parameters.add(parameterName);
        return parameterName;
    }
    /** Generated code example: f.default */
    generateValueCode(localName, dependencies) {
        dependencies.add(localName);
        return `${localName}.default`;
    }
    /** Generated code example: f.default({b: b}) */
    generateTemplateCode(localName, parametersCode, dependencies) {
        dependencies.add(localName);
        return `${localName}.default(${parametersCode})`;
    }
}
exports.JsonTemplateCompiler = JsonTemplateCompiler;
